/**
 * SEO component that queries for data with
 *  Gatsby's useStaticQuery React hook
 *
 * See: https://www.gatsbyjs.org/docs/use-static-query/
 */

import React, { useEffect, useMemo } from 'react';
import Helmet from 'react-helmet';
import { graphql, useStaticQuery } from 'gatsby';
import { cloudAssetUrl } from '@utils/cloudAssetUrl.util';
import { useIsMounted } from '@hooks/useIsMounted';
import { withWindow } from '@utils/withWindow';
import { segment } from '@static/js/joy';
import { Integrations } from '@static/js/types';
import { useTranslation } from 'react-i18next';
import { updateUrlQueryArg } from '@utils/queryString';
import { DEFAULT_LANGUAGE, languages } from '@i18n/languages';

type Priority = 'high' | 'low' | 'auto';
type SEOProps = {
  allowIndexing?: boolean;
  author?: string;
  canonicalURL: string;
  description?: string;
  email?: string;
  faqData?: ReadonlyArray<{ questionText: string; answerText: string }>;
  fbAppId?: string;
  keywords?: string;
  lang?: string;
  meta?: Array<{ name: string; content: string }>;
  name?: string;
  ogImage?: string;
  ogLogo?: string;
  preloadLinks?: [{ rel: string; href: string; as: string; fetchPriority: Priority }];
  title: string;
  url?: string;
};

export function Seo({
  allowIndexing,
  author,
  canonicalURL,
  description,
  lang,
  meta = [],
  name,
  ogImage = 'common/link-preview/home.png',
  ogLogo = 'common/brand/png_purple5/logo_xl.png',
  preloadLinks,
  title,
  url,
}: SEOProps) {
  const { site } = useStaticQuery(
    graphql`
      query {
        site {
          siteMetadata {
            allowIndexing
          }
        }
      }
    `
  );

  const { t, i18n } = useTranslation('seo');

  const metaDescription = description || t('description');
  title = title || t('title');
  name = name || t('name');
  author = author || t('author');
  url = url || t('url');
  allowIndexing = allowIndexing || site.siteMetadata.allowIndexing;
  lang = i18n.language;

  // the canonical URL should include the language query param for languages other than the default `en-US`
  const finalCanonicalURL = lang === DEFAULT_LANGUAGE ? canonicalURL : updateUrlQueryArg(canonicalURL, 'l', lang);

  const alternativeLanguageLinks = useMemo(() => {
    const links = languages
      .filter(({ isDefault }) => !isDefault) // filter out the default (`en-US`) language as its already included as `x-default`
      .map(({ languageCode }) => (
        <link key={languageCode} rel="alternate" href={updateUrlQueryArg(canonicalURL, 'l', languageCode)} hrefLang={languageCode} />
      ));

    links.push(<link key="x-default" rel="alternate" href={canonicalURL} hrefLang="x-default" />);
    return links;
  }, [canonicalURL]);

  const ldJSON = {
    '@context': 'http://schema.org/',
    '@type': 'WebSite',
    name: name + ' - ' + title,
    image: cloudAssetUrl('legacy/favicon/android-icon-192x192.png', { optimization: 'aggressive' }), // TODO: Pull image in from assets folder.
    description: metaDescription,
  };

  const isMounted = useIsMounted();

  useEffect(() => {
    withWindow((global) => {
      if (isMounted) {
        segment.sendTelemetryPageView(title, canonicalURL, global.document, {
          integrations: { [Integrations.Amplitude]: true },
        });
      }
    });
  }, [canonicalURL, isMounted, title]);

  return (
    <Helmet
      htmlAttributes={{
        lang,
      }}
      title={title}
      titleTemplate={`%s`}
      meta={[
        {
          name: `og:site_name`,
          content: name,
        },
        {
          name: `og:url`,
          content: url,
        },
        {
          name: `description`,
          content: metaDescription,
        },
        {
          name: `author`,
          content: author,
        },
        {
          property: `og:title`,
          content: title,
        },
        {
          property: `og:description`,
          content: metaDescription,
        },
        {
          property: `og:image`,
          content: cloudAssetUrl(ogImage),
        },
        {
          property: `og:logo`,
          content: cloudAssetUrl(ogLogo),
        },
        {
          property: `og:type`,
          content: `website`,
        },
        {
          name: `twitter:card`,
          content: `summary`,
        },
        {
          name: `twitter:creator`,
          content: author,
        },
        {
          name: `twitter:title`,
          content: title,
        },
        {
          name: `twitter:description`,
          content: metaDescription,
        },
        {
          name: 'viewport',
          content: 'width=device-width, initial-scale=1',
        },
        {
          name: 'google-site-verification',
          content: 'cxYjaIQ1P9y_DmFhAg1l5fevNpvwjWy8-wc-yNkDsCQ',
        },
        {
          name: 'p:domain_verify',
          content: '3c9b78164292cc9faa5b7e857775406d',
        },
        {
          name: 'msapplication-TileColor',
          content: '#ffffff',
        },
        {
          name: 'theme-color',
          content: '#ffffff',
        },
        {
          name: 'msapplication-TileImage',
          content: '/assets/favicon/ms-icon-144x144.png', // TODO: Add image asset to this project.
        },
        {
          name: 'robots',
          content: allowIndexing ? 'index' : 'noindex',
        },
      ].concat(meta)}
    >
      <link rel="canonical" href={finalCanonicalURL} />
      {alternativeLanguageLinks}
      <script type="application/ld+json">{JSON.stringify(ldJSON)}</script>
      {preloadLinks && preloadLinks.length > 0 && preloadLinks.map((link, index) => <link key={index} {...link} />)}
    </Helmet>
  );
}
